<!-- Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS -->

<template>
  <ion-page>
    <ion-content
      :fullscreen="true"
      class="content-scroll folder-content"
      :class="{
        searching: searchInputValue.length > 0,
        archived: workspaceInfo?.isArchived || workspaceInfo?.isTrashed,
      }"
    >
      <ms-action-bar
        id="folders-ms-action-bar"
        v-if="isLargeDisplay"
        :buttons="actionBarDesktop"
      >
        <div class="right-side">
          <ion-text
            class="button-medium read-only-info"
            v-if="workspaceInfo?.isArchived || workspaceInfo?.isTrashed"
          >
            <ion-icon
              :icon="eye"
              class="read-only-info__icon"
            />
            {{ $msTranslate('WorkspacesPage.archiveWorkspace.readOnly') }}
          </ion-text>
          <ms-search-input
            :placeholder="'FoldersPage.search.placeholder'"
            id="search-input-files"
            ref="searchInputRef"
            :debounce="300"
            @change="onSearchValueChange"
            v-model="searchPattern"
          />
          <workspace-role-tag
            v-if="!workspaceInfo?.isArchived"
            :role="ownRole"
            class="workspace-role-tag"
          />

          <div
            class="counter"
            v-show="!search"
          >
            <ion-text
              class="body"
              v-if="selectedFilesCount === 0"
            >
              {{
                $msTranslate({
                  key: 'FoldersPage.itemCount',
                  data: { count: folders.entriesCount() + files.entriesCount() },
                  count: folders.entriesCount() + files.entriesCount(),
                })
              }}
            </ion-text>
            <ion-text
              class="body item-selected"
              v-if="selectedFilesCount > 0"
            >
              {{ $msTranslate({ key: 'FoldersPage.itemSelectedCount', data: { count: selectedFilesCount }, count: selectedFilesCount }) }}
            </ion-text>
          </div>

          <ms-sorter
            v-show="!search"
            :sort-ascending="currentSortOrder"
            :key="`${currentSortProperty}-${currentSortOrder}`"
            label="FoldersPage.sort.byName"
            :options="msSorterOptions"
            :default-option="currentSortProperty"
            :sorter-labels="msSorterLabels"
            :sort-by-asc="currentSortOrder"
            @change="onSortChange"
          />

          <ms-grid-list-toggle
            v-show="!search"
            v-model="displayView"
            @update:model-value="onDisplayStateChange"
          />
        </div>
      </ms-action-bar>
      <small-display-selection-header
        v-if="workspaceInfo && isSmallDisplay && selectionEnabled"
        :title="getDisplayText()"
        @open-contextual-modal="openGlobalContextMenu"
        @select="selectAll"
        @unselect="unselectAll"
        @cancel-selection="onSelectionCancel"
        :some-selected="selectedFilesCount > 0"
      />
      <div class="main-container folder-container scroll">
        <file-inputs
          ref="fileInputs"
          @files-added="startImportFiles"
        />

        <ms-spinner
          v-show="querying"
          class="ms-spinner loading-files-content"
          title="FoldersPage.loading"
        />

        <div
          v-if="isSmallDisplay"
          class="mobile-filters"
        >
          <div class="mobile-filters-buttons">
            <ms-sorter
              v-if="!search"
              :key="`${currentSortProperty}-${currentSortOrder}`"
              :label="'FoldersPage.sort.byName'"
              :options="msSorterOptions"
              :default-option="currentSortProperty"
              :sorter-labels="msSorterLabels"
              :sort-by-asc="currentSortOrder"
              @change="onSortChange"
            />

            <ms-grid-list-toggle
              v-model="displayView"
              @update:model-value="onDisplayStateChange"
            />
          </div>
        </div>

        <div
          v-if="isSmallDisplay"
          class="search-mobile-container"
        >
          <ms-search-input
            :placeholder="'FoldersPage.search.placeholder'"
            :debounce="300"
            @change="onSearchValueChange"
            v-model="searchPattern"
            id="search-input-files"
            class="search-input-mobile"
          />
        </div>

        <div
          v-if="!querying && itemsToShow === 0"
          class="no-files body-lg"
        >
          <file-drop-zone
            :show-drop-message="true"
            @files-added="startImportFiles"
            @drop-as-reader="onDropAsReader"
            @global-menu-click="openGlobalContextMenu"
            :is-reader="isReadOnly"
          >
            <div
              class="error-list-folder"
              v-if="showErrorListPage"
            >
              <ms-image
                :image="ListFolderError"
                class="error-list-folder__image"
              />
              <ms-report-text
                :theme="MsReportTheme.Error"
                class="error-list-folder__text"
              >
                {{ $msTranslate('FoldersPage.errors.failedToListContent') }}
              </ms-report-text>
            </div>
            <div
              class="no-files-content"
              v-else
            >
              <ms-image :image="EmptyFolder" />
              <ion-text>
                {{ $msTranslate('FoldersPage.emptyFolder') }}
              </ion-text>
            </div>
          </file-drop-zone>
        </div>
        <div v-else-if="!querying && !showErrorListPage && !search">
          <div v-if="displayView === DisplayState.List && userInfo">
            <file-list-display
              ref="fileListDisplay"
              :files="files"
              :folders="folders"
              :own-profile="userInfo.currentProfile"
              :is-reader="isReadOnly"
              :selection-enabled="selectionEnabled && isSmallDisplay"
              :current-sort-order="currentSortOrder"
              :current-sort-property="currentSortProperty"
              :file-operations="fileOperationsCurrentDir"
              @open-item="onEntryClick"
              @menu-click="openEntryContextMenu"
              @files-added="startImportFiles"
              @global-menu-click="openGlobalContextMenu"
              @drop-as-reader="onDropAsReader"
              @sort-change="onSortChange"
            />
          </div>
          <div v-if="displayView === DisplayState.Grid">
            <file-grid-display
              ref="fileGridDisplay"
              :files="files"
              :folders="folders"
              :is-reader="isReadOnly"
              :selection-enabled="selectionEnabled && isSmallDisplay"
              :file-operations="fileOperationsCurrentDir"
              @open-item="onEntryClick"
              @menu-click="openEntryContextMenu"
              @files-added="startImportFiles"
              @global-menu-click="openGlobalContextMenu"
              @drop-as-reader="onDropAsReader"
            />
          </div>
        </div>
        <template v-if="search">
          <file-search-results
            :pattern="search.pattern"
            :search-results="search.results"
            :active="search.active"
            :multiple-workspaces="false"
            @item-click="onSearchResultClick"
            @menu-item-click="onSearchResultContextMenu"
            @update-pattern="search.pattern = $event"
          />
        </template>
      </div>
      <tab-bar-options
        v-if="customTabBar.isVisible.value"
        :actions="tabBarActions"
      />
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { entryNameValidator } from '@/common/validators';
import * as parsec from '@/parsec';
import {
  DisplayState,
  DocumentImport,
  EmptyFolder,
  EyeOpenIcon,
  I18n,
  MsActionBar,
  MsActionBarAction,
  MsGridListToggle,
  MsImage,
  MsModalResult,
  MsOptions,
  MsReportText,
  MsReportTheme,
  MsSearchInput,
  MsSorter,
  MsSorterChangeEvent,
  MsSpinner,
  RenameIcon,
  Translatable,
  asyncComputed,
  getTextFromUser,
  openSpinnerModal,
  useWindowSize,
} from 'megashark-lib';

import DocumentNew from '@/assets/images/add-document.svg?raw';
import ListFolderError from '@/assets/images/list-folder-error.svg?raw';
import { findAvailableFileName } from '@/common/file';
import { FileContentType, OPENABLE_FILES, isFileEditable } from '@/common/fileTypes';
import {
  EntryCollection,
  EntryModel,
  FileDropZone,
  FileGridDisplay,
  FileImportPopover,
  FileInputs,
  FileListDisplay,
  FileModel,
  FileSearchResults,
  FolderDefaultData,
  FolderModel,
  FoldersPageSavedData,
  ImportType,
  NewFilePopover,
  SortProperty,
  copyPathLinkToClipboard,
} from '@/components/files';
import { FileSearch } from '@/components/files/handler/viewer/types';
import { EntrySyncStatus, FileOperationCurrentFolder } from '@/components/files/types';
import SmallDisplaySelectionHeader from '@/components/header/SmallDisplaySelectionHeader.vue';
import { WorkspaceRoleTag } from '@/components/workspaces';
import {
  ClientInfo,
  EntryName,
  Path,
  WorkspaceCreateFolderErrorTag,
  WorkspaceRole,
  entryStat,
  getClientInfo,
  isDesktop,
  isWeb,
} from '@/parsec';
import { Routes, currentRouteIs, getCurrentRouteQuery, getDocumentPath, getWorkspaceHandle, navigateTo, watchRoute } from '@/router';
import { useFileContextMenu } from '@/services/contextMenu';
import { useFileActions } from '@/services/contextMenu/fileActions';
import {
  EntrySyncData,
  EventData,
  EventDistributor,
  EventDistributorKey,
  Events,
  MenuActionData,
  OpenContextualMenuData,
} from '@/services/eventDistributor';
import {
  DuplicatePolicy,
  FileEventRegistrationCanceller,
  FileOperationCopyData,
  FileOperationData,
  FileOperationDataType,
  FileOperationEventData,
  FileOperationEvents,
  FileOperationImportData,
  FileOperationManager,
  FileOperationManagerKey,
  FileOperationMoveData,
  FileOperationRestoreData,
} from '@/services/fileOperation';
import useHeaderControl from '@/services/headerControl';
import { HotkeyGroup, HotkeyManager, HotkeyManagerKey, Modifiers, Platforms } from '@/services/hotkeyManager';
import { Information, InformationLevel, InformationManager, InformationManagerKey, PresentationMode } from '@/services/informationManager';
import { OpenPathOptions } from '@/services/pathOpener';
import { StorageManager, StorageManagerKey } from '@/services/storageManager';
import { FileAction, FolderGlobalAction, getDuplicatePolicy, isFolderGlobalAction } from '@/views/files';
import { MenuAction, TabBarOptions, useCustomTabBar } from '@/views/menu';
import { IonContent, IonIcon, IonPage, IonText, popoverController } from '@ionic/vue';
import { arrowRedo, create, download, duplicate, eye, folderOpen, informationCircle, link, open, time, trashBin } from 'ionicons/icons';
import { Ref, computed, inject, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';

const customTabBar = useCustomTabBar();

const { isLargeDisplay, isSmallDisplay } = useWindowSize();
const { hideHeader, showHeader } = useHeaderControl();

const msSorterOptions: MsOptions = new MsOptions([
  { label: 'FoldersPage.sort.byName', key: SortProperty.Name },
  { label: 'FoldersPage.sort.byLastUpdate', key: SortProperty.LastUpdate },
  { label: 'FoldersPage.sort.byCreation', key: SortProperty.CreationDate },
  { label: 'FoldersPage.sort.bySize', key: SortProperty.Size },
]);

const msSorterLabels = {
  asc: 'FoldersPage.sort.asc',
  desc: 'FoldersPage.sort.desc',
};

const routeWatchCancel = watchRoute(async () => {
  if (!currentRouteIs(Routes.Documents)) {
    return;
  }

  const newPath = getDocumentPath();
  if (newPath === '') {
    return;
  }
  let samePath = true;
  if (newPath !== currentPath.value) {
    currentPath.value = newPath;
    samePath = false;
  }
  const workspaceHandle = getWorkspaceHandle();
  if (workspaceHandle) {
    if (workspaceInfo.value && workspaceHandle !== workspaceInfo.value.handle) {
      samePath = false;
    }
    const infoResult = await parsec.getWorkspaceInfo(workspaceHandle);
    if (infoResult.ok) {
      workspaceInfo.value = infoResult.value;
    }
  }

  const query = getCurrentRouteQuery();

  await listFolder({ selectFile: query.selectFile, sameFolder: samePath });
});

const contextMenu = useFileContextMenu();
const fileOperationManager: Ref<FileOperationManager> = inject(FileOperationManagerKey)!;
const hotkeyManager: HotkeyManager = inject(HotkeyManagerKey)!;
const informationManager: Ref<InformationManager> = inject(InformationManagerKey)!;
const storageManager: StorageManager = inject(StorageManagerKey)!;
const eventDistributor: Ref<EventDistributor> = inject(EventDistributorKey)!;

const FOLDERS_PAGE_DATA_KEY = 'FoldersPage';

const currentSortProperty = ref<SortProperty>(SortProperty.Name);
const currentSortOrder = ref(true);

const showErrorListPage = ref(false);
const userInfo: Ref<ClientInfo | null> = ref(null);
const fileOperations = ref<Array<FileOperationData>>([]);
const currentPath = ref('/');
const currentFolder: Ref<EntryName> = ref('/');
const folders = ref(new EntryCollection<FolderModel>());
const files = ref(new EntryCollection<FileModel>());
const displayView = ref(DisplayState.List);
const search = ref<FileSearch | undefined>(undefined);
let searchAborter: AbortController | undefined = undefined;
const searchPattern = ref('');
const searchInputValue = ref('');
const workspaceInfo: Ref<parsec.WorkspaceInfo | null> = ref(null);
// Init at true to avoid blinking while we're mounting the component
// but we're not loading the files yet.
const querying = ref(true);
const fileListDisplayRef = useTemplateRef<InstanceType<typeof FileListDisplay>>('fileListDisplay');
const fileGridDisplayRef = useTemplateRef<InstanceType<typeof FileGridDisplay>>('fileGridDisplay');

const searchInputRef = ref();
const fileInputsRef = useTemplateRef<InstanceType<typeof FileInputs>>('fileInputs');
let eventCbId: string | null = null;

const selectedFilesCount = computed(() => {
  return files.value.selectedCount() + folders.value.selectedCount();
});
const selectionEnabled = ref<boolean>(false);
const manualSelection = ref<boolean>(false);

const fileActions = useFileActions();

let hotkeys: HotkeyGroup | null = null;
let fileOpCanceller!: FileEventRegistrationCanceller;

const ownRole = computed(() => {
  return workspaceInfo.value ? workspaceInfo.value.selfRole : parsec.WorkspaceRole.Reader;
});

const isReadOnly = asyncComputed(async (): Promise<boolean> => {
  if (
    ownRole.value === parsec.WorkspaceRole.Reader ||
    workspaceInfo.value?.isArchived === true ||
    workspaceInfo.value?.isTrashed === true
  ) {
    return true;
  }
  return false;
});

const itemsToShow = computed(() => {
  return folders.value.entriesCount() + files.value.entriesCount() + fileOperationsCurrentDir.value.length;
});

const tabBarActions = computed(() => {
  const selectedEntries = getSelectedEntries();
  const actions: MenuAction[] = [];

  if (search.value) {
    return actions;
  }

  // disagreement between prettier and eslint
  actions.push(
    ...(isLargeDisplay.value && !isReadOnly.value && selectedEntries.length === 0
      ? [
          {
            label: 'FoldersPage.createFolder',
            icon: folderOpen,
            action: createFolder,
          },
        ]
      : []),
  );
  actions.push(
    ...(isLargeDisplay.value && !isReadOnly.value && selectedEntries.length === 0
      ? [
          {
            label: 'FoldersPage.createFile.button',
            image: DocumentNew,
            action: async (event: MouseEvent) => {
              await onCreateNewFileClicked(event);
            },
          },
        ]
      : []),
  );
  actions.push(
    ...(isLargeDisplay.value && !isReadOnly.value && selectedEntries.length === 0
      ? [
          {
            label: 'FoldersPage.import',
            image: DocumentImport,
            action: async (event: MouseEvent) => {
              await onImportClicked(event);
            },
          },
        ]
      : []),
  );
  actions.push(
    ...(selectedEntries.length === 1 && selectedEntries[0].isFile()
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionPreview',
            action: async () => await openEntry(selectedEntries[0], { skipViewers: false, readOnly: true }),
            icon: eye,
          },
        ]
      : []),
  );
  actions.push(
    ...(!isReadOnly.value && selectedEntries.length === 1 && selectedEntries[0].isFile() && isFileEditable(selectedEntries[0].name)
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionEdit',
            action: async () => await openEntry(selectedEntries[0], {}),
            icon: create,
          },
        ]
      : []),
  );
  actions.push(
    ...(!isReadOnly.value && selectedEntries.length === 1
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionRename',
            action: async () => await dispatchContextMenuAction(FileAction.Rename, selectedEntries),
            image: RenameIcon,
          },
        ]
      : []),
  );
  actions.push(
    ...(!isReadOnly.value && selectedEntries.length >= 1
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionMoveTo',
            action: async () => await dispatchContextMenuAction(FileAction.MoveTo, selectedEntries),
            icon: arrowRedo,
          },
        ]
      : []),
  );
  actions.push(
    ...(!isReadOnly.value && selectedEntries.length >= 1
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionMakeACopy',
            action: async () => await dispatchContextMenuAction(FileAction.MakeACopy, selectedEntries),
            icon: duplicate,
          },
        ]
      : []),
  );
  actions.push(
    ...(!isReadOnly.value && selectedEntries.length >= 1
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionDelete',
            action: async () => await dispatchContextMenuAction(FileAction.Delete, selectedEntries),
            icon: trashBin,
            danger: true,
          },
        ]
      : []),
  );
  actions.push(
    ...(isWeb() && selectedEntries.length >= 1
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionDownload',
            action: async () => await dispatchContextMenuAction(FileAction.Download, selectedEntries),
            icon: download,
          },
        ]
      : []),
  );
  actions.push(
    ...(isDesktop() && selectedEntries.length === 1
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionSeeInExplorer',
            action: async () => await dispatchContextMenuAction(FileAction.SeeInExplorer, selectedEntries),
            image: EyeOpenIcon,
          },
        ]
      : []),
  );
  actions.push(
    ...(isDesktop() && selectedEntries.length === 1
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionOpen',
            action: async () => await openEntry(selectedEntries[0], { skipViewers: true }),
            icon: open,
          },
        ]
      : []),
  );

  actions.push(
    ...(selectedEntries.length === 1
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionHistory',
            action: async () => await dispatchContextMenuAction(FileAction.ShowHistory, selectedEntries),
            icon: time,
          },
        ]
      : []),
  );
  actions.push(
    ...(selectedEntries.length === 1
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionCopyLink',
            action: async () => await dispatchContextMenuAction(FileAction.CopyLink, selectedEntries),
            icon: link,
          },
        ]
      : []),
  );
  actions.push(
    ...(selectedEntries.length === 1
      ? [
          {
            label: 'FoldersPage.fileContextMenu.actionDetails',
            action: async () => await dispatchContextMenuAction(FileAction.ShowDetails, selectedEntries),
            icon: informationCircle,
          },
        ]
      : []),
  );
  return actions;
});

const actionBarDesktop = computed(() => {
  return tabBarActions.value.map((action) => {
    return {
      label: action.label,
      image: action.image,
      icon: action.icon,
      onClick: action.action,
    } as MsActionBarAction;
  });
});

const fileOperationsCurrentDir = asyncComputed(async () => {
  const entryNames: Array<FileOperationCurrentFolder> = [];

  for (const op of fileOperations.value) {
    // Early break if it's not the same workspace
    if (op.workspaceId !== workspaceInfo.value?.id) {
      continue;
    }
    if (op.type === FileOperationDataType.Import) {
      const importOp = op as FileOperationImportData;
      // Early break if the current path is not included in the destination
      if (!importOp.destination.startsWith(currentPath.value)) {
        continue;
      }
      for (const file of importOp.files) {
        const dest = Path.quickJoin(importOp.destination, (file as any).relativePath);
        const filePath = await Path.parent(dest);
        if (Path.areSame(filePath, currentPath.value)) {
          entryNames.push({ type: op.type, entryName: file.name });
        }
      }
    } else if (op.type === FileOperationDataType.Restore) {
      const restoreOp = op as FileOperationRestoreData;
      for (const entry of restoreOp.entries) {
        if (Path.areSame(await Path.parent(entry.path), currentPath.value)) {
          entryNames.push({ type: op.type, entryName: entry.name });
        }
      }
    } else if (op.type === FileOperationDataType.Copy || op.type === FileOperationDataType.Move) {
      let opData: FileOperationCopyData | FileOperationMoveData;
      if (op.type === FileOperationDataType.Copy) {
        opData = op as FileOperationCopyData;
      } else {
        opData = op as FileOperationMoveData;
      }
      // Early break if the destination is not the current path
      if (!Path.areSame(opData.destination, currentPath.value)) {
        continue;
      }
      for (const entry of opData.sources) {
        entryNames.push({ type: op.type, entryName: entry.name });
      }
    }
  }
  return entryNames;
});

const headerWatchCancel = watch([isSmallDisplay, selectionEnabled], () => {
  isSmallDisplay.value && selectionEnabled.value ? hideHeader() : showHeader();
});

const tabBarWatchCancel = watch([isSmallDisplay, selectedFilesCount], () => {
  if (isSmallDisplay.value && selectedFilesCount.value >= 1) {
    customTabBar.show();
  } else {
    customTabBar.hide();
  }
});

const selectedCountWatchCancel = watch([selectedFilesCount, manualSelection], () => {
  selectionEnabled.value = selectedFilesCount.value > 0 || manualSelection.value === true;
});

async function defineShortcuts(): Promise<void> {
  hotkeys = hotkeyManager.newHotkeys();
  hotkeys.add(
    { key: 'o', modifiers: Modifiers.Ctrl, platforms: Platforms.Desktop | Platforms.Web, disableIfModal: true, route: Routes.Documents },
    async () => {
      await fileInputsRef.value?.importFiles();
    },
  );
  hotkeys.add(
    {
      key: 'o',
      modifiers: Modifiers.Ctrl | Modifiers.Shift,
      platforms: Platforms.Desktop | Platforms.Web,
      disableIfModal: true,
      route: Routes.Documents,
    },
    async () => {
      await fileInputsRef.value?.importFolder();
    },
  );
  hotkeys.add(
    { key: 'enter', modifiers: Modifiers.None, platforms: Platforms.MacOS, disableIfModal: true, route: Routes.Documents },
    async () => {
      if (getSelectedEntries().length === 1) {
        await dispatchContextMenuAction(FileAction.Rename, getSelectedEntries());
      }
    },
  );
  hotkeys.add(
    { key: 'f2', modifiers: Modifiers.None, platforms: Platforms.Windows | Platforms.Linux, disableIfModal: true, route: Routes.Documents },
    async () => {
      if (getSelectedEntries().length === 1) {
        await dispatchContextMenuAction(FileAction.Rename, getSelectedEntries());
      }
    },
  );
  hotkeys.add(
    { key: 'i', modifiers: Modifiers.Ctrl | Modifiers.Shift, platforms: Platforms.Desktop, disableIfModal: true, route: Routes.Documents },
    async () => {
      if (getSelectedEntries().length === 1) {
        await dispatchContextMenuAction(FileAction.ShowDetails, getSelectedEntries());
      }
    },
  );
  // TODO find appropriate shortcuts for MakeACopy and MoveTo, knowing that each of these opens the console on Chrome and Firefox:
  // - ctrl + shift + C
  // - cmd + shift + C
  // - cmd + option + C

  // hotkeys.add(
  //   { key: 'c', modifiers: Modifiers.Ctrl, platforms: Platforms.Desktop, disableIfModal: true, route: Routes.Documents },
  //   async () => await dispatchContextMenuAction(FileAction.MakeACopy, getSelectedEntries()),
  // );
  // hotkeys.add(
  //   { key: 'x', modifiers: Modifiers.Ctrl, platforms: Platforms.Desktop, disableIfModal: true, route: Routes.Documents },
  //   async () => await dispatchContextMenuAction(FileAction.MoveTo, getSelectedEntries()),
  // );
  hotkeys.add(
    { key: 'l', modifiers: Modifiers.Ctrl, platforms: Platforms.Desktop | Platforms.Web, disableIfModal: true, route: Routes.Documents },
    async () => {
      if (getSelectedEntries().length === 1) {
        await dispatchContextMenuAction(FileAction.CopyLink, getSelectedEntries());
      }
    },
  );
  hotkeys.add(
    {
      key: 'delete',
      modifiers: Modifiers.None,
      platforms: Platforms.Windows | Platforms.Linux | Platforms.Web,
      disableIfModal: true,
      route: Routes.Documents,
    },
    async () => await dispatchContextMenuAction(FileAction.Delete, getSelectedEntries()),
  );
  hotkeys.add(
    { key: 'backspace', modifiers: Modifiers.Ctrl, platforms: Platforms.MacOS, disableIfModal: true, route: Routes.Documents },
    async () => await dispatchContextMenuAction(FileAction.Delete, getSelectedEntries()),
  );
  hotkeys.add(
    { key: 'g', modifiers: Modifiers.Ctrl, platforms: Platforms.Desktop, disableIfModal: true, route: Routes.Documents },
    async () => {
      displayView.value = displayView.value === DisplayState.Grid ? DisplayState.List : DisplayState.Grid;
    },
  );
  hotkeys.add(
    { key: 'a', modifiers: Modifiers.Ctrl, platforms: Platforms.Desktop, disableIfModal: true, route: Routes.Documents },
    async () => {
      files.value.hasAllSelected() && folders.value.hasAllSelected() ? unselectAll() : selectAll();
    },
  );
  hotkeys.add(
    { key: 'f', modifiers: Modifiers.Ctrl, platforms: Platforms.Web | Platforms.Desktop, disableIfModal: true, route: Routes.Documents },
    async () => await searchInputRef.value?.setFocus(),
  );
}

async function handleEvents(event: Events, data?: EventData): Promise<void> {
  if (event === Events.EntryUpdated) {
    await listFolder({ sameFolder: true });
  } else if (event === Events.WorkspaceUpdated && workspaceInfo.value) {
    await updateWorkspaceInfo(workspaceInfo.value.handle);
  } else if (event === Events.EntrySynced || event === Events.EntrySyncStarted || event === Events.EntrySyncProgress) {
    const syncedData = data as EntrySyncData;
    if (!workspaceInfo.value || workspaceInfo.value.id !== syncedData.workspaceId) {
      return;
    }
    if (syncedData.way === 'inbound') {
      await listFolder({ sameFolder: true });
    } else {
      let entry: EntryModel | undefined = files.value.getEntries().find((e) => e.id === syncedData.entryId);
      if (!entry) {
        entry = folders.value.getEntries().find((e) => e.id === syncedData.entryId);
      }
      if (entry && workspaceInfo.value) {
        if (event === Events.EntrySynced) {
          entry.needSync = false;
          entry.syncProgress = undefined;
          entry.syncStatus = EntrySyncStatus.Synced;
        } else if (event === Events.EntrySyncProgress || event === Events.EntrySyncStarted) {
          entry.needSync = true;
          entry.syncStatus = EntrySyncStatus.Uploading;
          entry.syncProgress = syncedData.progress;
        }
      }
    }
  } else if (event === Events.MenuAction) {
    const menuAction = (data as MenuActionData).action;
    if (isFolderGlobalAction(menuAction.action)) {
      await performFolderAction(menuAction.action);
    }
  } else if (event === Events.OpenContextMenu) {
    await openGlobalContextMenu((data as OpenContextualMenuData).event);
  }
}

function getDisplayText(): Translatable {
  if (files.value.selectedCount() > 0 || folders.value.selectedCount() > 0) {
    return {
      key: 'FoldersPage.itemSelectedCount',
      data: { count: selectedFilesCount.value },
      count: selectedFilesCount.value,
    };
  } else {
    return currentFolder.value !== '/'
      ? I18n.valueAsTranslatable(currentFolder.value)
      : I18n.valueAsTranslatable(workspaceInfo.value?.name);
  }
}

onMounted(async () => {
  const componentData = await storageManager.retrieveComponentData<FoldersPageSavedData>(FOLDERS_PAGE_DATA_KEY, FolderDefaultData);

  displayView.value = componentData.displayState;
  currentSortProperty.value = componentData.sortProperty;
  currentSortOrder.value = componentData.sortAscending;

  await defineShortcuts();

  eventCbId = await eventDistributor.value.registerCallback(
    [
      Events.EntryUpdated,
      Events.WorkspaceUpdated,
      Events.EntrySynced,
      Events.EntrySyncStarted,
      Events.MenuAction,
      Events.EntrySyncProgress,
      Events.OpenContextMenu,
    ],
    handleEvents,
  );

  const workspaceHandle = getWorkspaceHandle();
  if (workspaceHandle) {
    const infoResult = await parsec.getWorkspaceInfo(workspaceHandle);
    if (infoResult.ok) {
      workspaceInfo.value = infoResult.value;
    }
  }

  const clientInfoResult = await getClientInfo();
  if (clientInfoResult.ok) {
    userInfo.value = clientInfoResult.value;
  } else {
    window.nativeAPI.log('error', `Failed to retrieve user info ${JSON.stringify(clientInfoResult.error)}`);
  }

  fileOpCanceller = await fileOperationManager.value.registerCallback(onFileOperationEvent);
  currentPath.value = getDocumentPath();
  const query = getCurrentRouteQuery();
  await listFolder({ selectFile: query.selectFile });
});

onUnmounted(async () => {
  if (hotkeys) {
    hotkeyManager.unregister(hotkeys);
  }
  fileOpCanceller.cancel();
  customTabBar.hide();
  selectionEnabled.value = false;
  routeWatchCancel();
  tabBarWatchCancel();
  headerWatchCancel();
  selectedCountWatchCancel();
  if (eventCbId) {
    eventDistributor.value.removeCallback(eventCbId);
  }
});

async function updateWorkspaceInfo(handle: parsec.WorkspaceHandle): Promise<void> {
  const workspaceResult = await parsec.getWorkspaceInfo(handle);

  if (!workspaceResult.ok) {
    const listWorkspacesResult = await parsec.listWorkspaces();

    if (listWorkspacesResult.ok && !listWorkspacesResult.value.find((wk) => wk.handle === handle)) {
      // Not found, probably means that we've been excluded from the workspace
      await informationManager.value.present(
        new Information({
          message: {
            key: 'FoldersPage.events.workspaceUnshared',
            data: {
              name: workspaceInfo.value?.name,
            },
          },
          level: InformationLevel.Error,
        }),
        PresentationMode.Modal,
      );
    } else {
      await informationManager.value.present(
        new Information({
          message: {
            key: 'FoldersPage.errors.failedToListWorkspaces',
            data: {
              reason: workspaceResult.error.tag,
            },
          },
          level: InformationLevel.Info,
        }),
        PresentationMode.Toast,
      );
    }
    await navigateTo(Routes.Workspaces);
    return;
  }
  // display a toast if the role has been changed in a significant manner: Reader to something else, or something else to Reader
  if (workspaceInfo.value?.selfRole === WorkspaceRole.Reader && workspaceResult.value.selfRole !== WorkspaceRole.Reader) {
    await informationManager.value.present(
      new Information({
        message: {
          key: 'FoldersPage.events.roleUpdateNoLongerReader',
        },
        level: InformationLevel.Info,
      }),
      PresentationMode.Toast,
    );
  } else if (
    workspaceInfo.value &&
    workspaceInfo.value.selfRole !== WorkspaceRole.Reader &&
    workspaceResult.value.selfRole === WorkspaceRole.Reader
  ) {
    await informationManager.value.present(
      new Information({
        message: {
          key: 'FoldersPage.events.roleUpdateNowReader',
        },
        level: InformationLevel.Warning,
      }),
      PresentationMode.Toast,
    );
  }
  workspaceInfo.value = workspaceResult.value;
}

async function onDisplayStateChange(): Promise<void> {
  await storeComponentData();
}

async function onSortChange(event: MsSorterChangeEvent): Promise<void> {
  currentSortProperty.value = event.option.key;
  currentSortOrder.value = event.sortByAsc;
  folders.value.sort(currentSortProperty.value, currentSortOrder.value);
  files.value.sort(currentSortProperty.value, currentSortOrder.value);
  await storeComponentData();
}

async function storeComponentData(): Promise<void> {
  await storageManager.storeComponentData<FoldersPageSavedData>(FOLDERS_PAGE_DATA_KEY, {
    displayState: displayView.value,
    sortProperty: currentSortProperty.value,
    sortAscending: currentSortOrder.value,
  });
}

async function onFileOperationEvent(
  event: FileOperationEvents,
  operationData?: FileOperationData,
  _eventData?: FileOperationEventData,
): Promise<void> {
  if (!operationData) {
    if (event !== FileOperationEvents.AllFinished) {
      window.nativeAPI.log('warn', `Event ${event} received without operation data`);
    }
    return;
  }
  switch (event) {
    case FileOperationEvents.Added:
      fileOperations.value.push(operationData);
      break;
    case FileOperationEvents.Updated: {
      const idx = fileOperations.value.findIndex((op) => op.id === operationData.id);
      if (idx !== -1) {
        fileOperations.value.splice(idx, 1);
      }
      fileOperations.value.push(operationData);
      break;
    }
    case FileOperationEvents.Progress:
      if (fileOperations.value.find((op) => op.id === operationData.id) === undefined) {
        fileOperations.value.push(operationData);
      }
      break;
    case FileOperationEvents.Cancelled:
    case FileOperationEvents.Failed: {
      const index = fileOperations.value.findIndex((item) => item.id === operationData.id);
      if (index !== -1) {
        fileOperations.value.splice(index, 1);
      }
      break;
    }
    case FileOperationEvents.Finished: {
      const index = fileOperations.value.findIndex((item) => item.id === operationData.id);
      if (index === -1) {
        break;
      }
      fileOperations.value.splice(index, 1);
      await listFolder({ sameFolder: true });
      break;
    }
  }
}

async function startImportFiles(gen: AsyncGenerator<File[]>, destinationFolder?: EntryName): Promise<void> {
  if (!workspaceInfo.value) {
    return;
  }

  const preparedImport = await fileOperationManager.value.prepareImport(
    workspaceInfo.value.handle,
    [],
    destinationFolder ? await Path.join(currentPath.value, destinationFolder) : currentPath.value,
  );

  if (!preparedImport) {
    return;
  }

  const existing: Array<File> = [];
  let destination = currentPath.value;
  if (destinationFolder && destinationFolder !== '/') {
    destination = await Path.join(destination, destinationFolder);
  }

  for await (const files of gen) {
    // Reverse iteration to be able to remove entries from the list
    for (let i = files.length - 1; i >= 0; i--) {
      const file = files[i];
      if (!(file as any).relativePath) {
        (file as any).relativePath = `/${file.name}`;
      }
      const confined = await parsec.isPathConfined((file as any).relativePath);
      if (confined) {
        files.splice(i, 1);
        continue;
      }
      const importPath = Path.quickJoin(destination, (file as any).relativePath);
      const result = await entryStat(workspaceInfo.value.handle, importPath);
      if (result.ok) {
        existing.push(file);
      }
    }

    if (files.length === 0) {
      continue;
    }
    preparedImport.addFiles(files);
  }

  if (existing.length > 0) {
    preparedImport.data.dupPolicy = await getDuplicatePolicy(existing);
  }

  if (preparedImport.data.dupPolicy === DuplicatePolicy.Ignore) {
    preparedImport.removeFiles(existing);
  }

  if (preparedImport.data.files.length === 0) {
    informationManager.value.present(
      new Information({
        message: 'FoldersPage.noFilesToImport',
        level: InformationLevel.Info,
      }),
      PresentationMode.Toast,
    );
    preparedImport.cancel();
    return;
  }

  preparedImport.finalize();
}

async function listFolder(options?: { selectFile?: EntryName; sameFolder?: boolean }): Promise<void> {
  if (currentPath.value) {
    currentFolder.value = (await Path.filename(currentPath.value)) ?? '/';
  }

  if (!workspaceInfo.value) {
    return;
  }
  if (!currentRouteIs(Routes.Documents)) {
    return;
  }
  /*
   * If the folder didn't change, we don't need to show a spinner
   * as it will result in blinking.
   */
  if (!options || !options.sameFolder) {
    querying.value = true;
  }
  const result = await parsec.statFolderChildren(workspaceInfo.value.handle, currentPath.value);
  if (result.ok) {
    const newFolders: FolderModel[] = [];
    const newFiles: FileModel[] = [];

    for (const childStat of result.value) {
      const childName = childStat.name;
      if (childStat.isFile()) {
        (childStat as FileModel).isSelected = Boolean(options && options.selectFile && options.selectFile === childName);
        (childStat as FileModel).syncStatus = !childStat.needSync ? EntrySyncStatus.Synced : undefined;
        newFiles.push(childStat as FileModel);
      } else {
        (childStat as FolderModel).isSelected = Boolean(options && options.selectFile && options.selectFile === childName);
        (childStat as FolderModel).syncStatus = !childStat.needSync ? EntrySyncStatus.Synced : undefined;
        newFolders.push(childStat as FolderModel);
      }
    }
    folders.value.smartUpdate(newFolders);
    files.value.smartUpdate(newFiles);
    folders.value.sort(currentSortProperty.value, currentSortOrder.value);
    files.value.sort(currentSortProperty.value, currentSortOrder.value);
    showErrorListPage.value = false;
  } else {
    files.value.clear();
    folders.value.clear();
    showErrorListPage.value = true;
    // This happens when the handle becomes invalid (if we're logging out for example) and the app tries to refresh at the same
    // time. Logging out while importing files is a good example of that: logging out will cancel the imports which will trigger
    // a refresh.
  }
  querying.value = false;
  if (options && options.selectFile) {
    // ref are not set if querying is true, so after querying has been set to false, we force an update
    await nextTick();
    if (fileListDisplayRef.value) {
      await fileListDisplayRef.value.scrollToSelected();
    } else if (fileGridDisplayRef.value) {
      await fileGridDisplayRef.value.scrollToSelected();
    }
  }
}

async function onSearchResultClick(entry: parsec.SearchResult): Promise<void> {
  await onSelectionCancel();
  if (!entry.stats.isFile()) {
    searchPattern.value = '';
    await cancelSearch();
  }
  await onEntryClick({ ...entry.stats, isSelected: false });
}

async function createNewFile(name: EntryName, fileType: FileContentType): Promise<void> {
  if (!workspaceInfo.value) {
    return;
  }
  const modal = await openSpinnerModal();
  try {
    if ([FileContentType.Document, FileContentType.Presentation, FileContentType.Spreadsheet].includes(fileType)) {
      let template: Uint8Array;
      switch (fileType) {
        case FileContentType.Document:
          template = (await import('@/parsec/file_templates/docx_template')).default;
          break;
        case FileContentType.Spreadsheet:
          template = (await import('@/parsec/file_templates/xlsx_template')).default;
          break;
        case FileContentType.Presentation:
          template = (await import('@/parsec/file_templates/pptx_template')).default;
          break;
        default:
          return;
      }
      const tmpPath = await parsec.getTemporaryPath(currentPath.value);
      const fdResult = await parsec.openFile(workspaceInfo.value.handle, tmpPath, { createNew: true, create: true, write: true });
      if (!fdResult.ok) {
        informationManager.value.present(
          new Information({
            message:
              fdResult.error.tag === parsec.WorkspaceOpenFileErrorTag.EntryExistsInCreateNewMode
                ? 'FoldersPage.errors.createFileFailedAlreadyExists'
                : 'FoldersPage.errors.createFileFailed',
            level: InformationLevel.Error,
          }),
          PresentationMode.Toast,
        );
        return;
      }
      try {
        const writeResult = await parsec.writeFile(workspaceInfo.value.handle, fdResult.value, 0, template);
        if (!writeResult.ok) {
          informationManager.value.present(
            new Information({
              message: 'FoldersPage.errors.createFileFailed',
              level: InformationLevel.Error,
            }),
            PresentationMode.Toast,
          );
          await parsec.deleteFile(workspaceInfo.value.handle, tmpPath);
        } else {
          await parsec.rename(workspaceInfo.value.handle, tmpPath, name, true);
        }
      } finally {
        await parsec.closeFile(workspaceInfo.value.handle, fdResult.value);
      }
    } else if (fileType === FileContentType.Text) {
      const path = await parsec.Path.join(currentPath.value, name);
      const createResult = await parsec.createFile(workspaceInfo.value.handle, path);
      if (!createResult.ok) {
        informationManager.value.present(
          new Information({
            message: 'FoldersPage.errors.createFileFailed',
            level: InformationLevel.Error,
          }),
          PresentationMode.Toast,
        );
      }
    } else {
      window.nativeAPI.log('error', `Cannot create a new document of type ${fileType}`);
    }
  } finally {
    await modal.dismiss();
  }
  await listFolder({ sameFolder: true });
}

async function onEntryClick(entry: EntryModel): Promise<void> {
  if (!workspaceInfo.value) {
    return;
  }

  if (!entry.isFile()) {
    navigateTo(Routes.Documents, {
      query: { documentPath: entry.path, workspaceHandle: workspaceInfo.value?.handle },
    });
  } else {
    const config = await storageManager.retrieveConfig();
    await openEntry(entry, { skipViewers: config.skipViewers, readOnly: isReadOnly.value });
  }
}

async function createFolder(): Promise<void> {
  if (!workspaceInfo.value) {
    return;
  }
  const folderName = await getTextFromUser(
    {
      title: 'FoldersPage.CreateFolderModal.title',
      trim: true,
      validator: entryNameValidator(false, {
        checkExists: { workspaceHandle: workspaceInfo.value.handle, path: currentPath.value },
        checkConfined: true,
      }),
      inputLabel: 'FoldersPage.CreateFolderModal.label',
      placeholder: 'FoldersPage.CreateFolderModal.placeholder',
      okButtonText: 'FoldersPage.CreateFolderModal.create',
    },
    isLargeDisplay.value,
  );

  if (!folderName) {
    return;
  }
  const folderPath = await parsec.Path.join(currentPath.value, folderName);
  const result = await parsec.createFolder(workspaceInfo.value.handle, folderPath);
  if (!result.ok) {
    let message: Translatable = { key: 'FoldersPage.errors.createFolderFailed', data: { name: folderName } };
    switch (result.error.tag) {
      case WorkspaceCreateFolderErrorTag.EntryExists: {
        message = { key: 'FoldersPage.errors.createFolderAlreadyExists', data: { name: folderName } };
        break;
      }
      default:
        break;
    }
    informationManager.value.present(
      new Information({
        message: message,
        level: InformationLevel.Error,
      }),
      PresentationMode.Toast,
    );
  }
  await listFolder({ sameFolder: true });
}

async function onImportClicked(event: Event): Promise<void> {
  const popover = await popoverController.create({
    component: FileImportPopover,
    cssClass: 'import-popover',
    event: event,
    alignment: 'start',
    showBackdrop: false,
  });
  await popover.present();
  const result = await popover.onDidDismiss();
  await popover.dismiss();
  if (result.role !== MsModalResult.Confirm) {
    return;
  }
  if (result.data.type === ImportType.Files) {
    await fileInputsRef.value?.importFiles();
  } else if (result.data.type === ImportType.Folder) {
    await fileInputsRef.value?.importFolder();
  }
}

async function getNewFileName(fileType: FileContentType): Promise<string | null> {
  if (!workspaceInfo.value) {
    return null;
  }

  let defaultNewName = '';
  let ext = '';
  let allowedExts: Array<string> = [];

  switch (fileType) {
    case FileContentType.Document:
      defaultNewName = I18n.translate('FoldersPage.createFile.defaultName.document');
      ext = 'docx';
      allowedExts = [ext];
      break;
    case FileContentType.Spreadsheet:
      defaultNewName = I18n.translate('FoldersPage.createFile.defaultName.spreadsheet');
      ext = 'xlsx';
      allowedExts = [ext];
      break;
    case FileContentType.Presentation:
      defaultNewName = I18n.translate('FoldersPage.createFile.defaultName.presentation');
      ext = 'pptx';
      allowedExts = [ext];
      break;
    case FileContentType.Text:
      defaultNewName = I18n.translate('FoldersPage.createFile.defaultName.text');
      ext = 'txt';
      allowedExts = OPENABLE_FILES.TEXTS;
      break;
    default:
      break;
  }
  if (!defaultNewName) {
    window.nativeAPI.log('warn', 'No default name for file');
    return null;
  }

  const finalDefaultName =
    (await findAvailableFileName({
      handle: workspaceInfo.value.handle,
      fileName: `${defaultNewName}.${ext}`,
      path: currentPath.value,
    })) ?? `${defaultNewName}.${ext}`;

  const newName = await getTextFromUser(
    {
      title: 'FoldersPage.createFile.fileName',
      trim: true,
      validator: entryNameValidator(true, {
        checkExists: { workspaceHandle: workspaceInfo.value.handle, path: currentPath.value },
        checkConfined: true,
        allowedExtensions: allowedExts,
      }),
      inputLabel: 'FoldersPage.createFile.label',
      placeholder: 'FoldersPage.createFile.placeholder',
      okButtonText: 'FoldersPage.createFile.create',
      defaultValue: finalDefaultName,
      selectionRange: [0, Path.filenameWithoutExtension(finalDefaultName).length],
    },
    isLargeDisplay.value,
  );
  if (!newName) {
    return null;
  }
  return newName;
}

async function onCreateNewFileClicked(event: Event): Promise<void> {
  const popover = await popoverController.create({
    component: NewFilePopover,
    cssClass: 'new-file-popover',
    event: event,
    alignment: 'start',
    showBackdrop: false,
  });
  await popover.present();
  const result = await popover.onDidDismiss();
  await popover.dismiss();
  if (result.role !== MsModalResult.Confirm) {
    return;
  }
  const name = await getNewFileName(result.data.fileType);
  if (!name) {
    return;
  }
  await createNewFile(name, result.data.fileType);
}

function getSelectedEntries(): EntryModel[] {
  return [...folders.value.getSelectedEntries(), ...files.value.getSelectedEntries()];
}

async function openEntry(entryToOpen: EntryModel, options: OpenPathOptions): Promise<void> {
  if (!workspaceInfo.value) {
    window.nativeAPI.log('warn', 'Trying to open an entry but missing workspace info.');
    return;
  }
  await fileActions.openEntry(entryToOpen, options, workspaceInfo.value);
  selectionEnabled.value = false;
}

async function performFolderAction(action: FolderGlobalAction): Promise<void> {
  if (!workspaceInfo.value) {
    return;
  }
  switch (action) {
    case FolderGlobalAction.CreateFolder:
      return await createFolder();
    case FolderGlobalAction.ImportFiles:
      return await fileInputsRef.value?.importFiles();
    case FolderGlobalAction.ImportFolder:
      return await fileInputsRef.value?.importFolder();
    case FolderGlobalAction.OpenInExplorer:
      return await fileActions.seeInExplorer(currentPath.value, workspaceInfo.value);
    case FolderGlobalAction.ToggleSelect:
      return await toggleSelection();
    case FolderGlobalAction.SelectAll:
      return await selectAll();
    case FolderGlobalAction.Share:
      return await shareEntries();
    case FolderGlobalAction.CreateFileDocument: {
      const name = await getNewFileName(FileContentType.Document);
      if (name) {
        await createNewFile(name, FileContentType.Document);
      }
      break;
    }
    case FolderGlobalAction.CreateFileSpreadsheet: {
      const name = await getNewFileName(FileContentType.Spreadsheet);
      if (name) {
        await createNewFile(name, FileContentType.Spreadsheet);
      }
      break;
    }
    case FolderGlobalAction.CreateFilePresentation: {
      const name = await getNewFileName(FileContentType.Presentation);
      if (name) {
        await createNewFile(name, FileContentType.Presentation);
      }
      break;
    }
    case FolderGlobalAction.CreateFileText: {
      const name = await getNewFileName(FileContentType.Text);
      if (name) {
        await createNewFile(name, FileContentType.Text);
      }
      break;
    }
    default:
      break;
  }
}

async function openGlobalContextMenu(event: Event): Promise<void> {
  if (!workspaceInfo.value) {
    return;
  }
  const action = await contextMenu.openGlobalContextMenu(
    event,
    isReadOnly.value,
    folders.value.entriesCount() + files.value.entriesCount() === 0,
  );

  if (!action) {
    return;
  }
  await performFolderAction(action);
}

async function dispatchContextMenuAction(action: FileAction, entries: Array<parsec.EntryStat>): Promise<void> {
  if (!workspaceInfo.value || !userInfo.value || entries.length === 0) {
    return;
  }
  await contextMenu.dispatchContextMenuAction(action, entries, workspaceInfo.value, userInfo.value.currentProfile);
  if ([FileAction.ShowHistory, FileAction.Open, FileAction.MakeACopy, FileAction.MoveTo].includes(action)) {
    selectionEnabled.value = false;
  }
  if ([FileAction.Rename, FileAction.Download, FileAction.Delete].includes(action)) {
    await onSelectionCancel();
  }
  if ([FileAction.Delete, FileAction.Rename, FileAction.MoveTo, FileAction.MakeACopy].includes(action)) {
    if (search.value) {
      await startSearch(searchPattern.value);
    }
    await listFolder({ sameFolder: true });
  }
  if ([FileAction.SeeInExplorer].includes(action)) {
    searchPattern.value = '';
    await cancelSearch();
  }
}

async function onSearchResultContextMenu(event: Event, entry: parsec.SearchResult, onFinished?: () => void): Promise<void> {
  await onSelectionCancel();
  const action = await contextMenu.openEntryContextMenu(event, [entry.stats], isReadOnly.value, true);

  if (action) {
    await dispatchContextMenuAction(action, [entry.stats]);
  }
  if (onFinished) {
    onFinished();
  }
}

async function openEntryContextMenu(event: Event, entry: EntryModel, onFinished?: () => void): Promise<void> {
  const selectedEntries = getSelectedEntries();

  const entries: EntryModel[] = [];

  if (!selectedEntries.includes(entry)) {
    await onSelectionCancel();
    entries.push(entry);
  } else {
    entries.push(...selectedEntries);
  }

  const action = await contextMenu.openEntryContextMenu(event, entries, isReadOnly.value);

  if (action) {
    await dispatchContextMenuAction(action, entries);
  }
  if (onFinished) {
    onFinished();
  }
}

async function toggleSelection(): Promise<void> {
  selectionEnabled.value = !selectionEnabled.value;
  if (selectionEnabled.value === false) {
    manualSelection.value = false;
    await unselectAll();
  } else {
    manualSelection.value = true;
  }
}

async function onSelectionCancel(): Promise<void> {
  await unselectAll();
  manualSelection.value = false;
}

async function selectAll(): Promise<void> {
  manualSelection.value = true;
  folders.value.selectAll(true);
  files.value.selectAll(true);
}

async function unselectAll(): Promise<void> {
  manualSelection.value = true;
  folders.value.selectAll(false);
  files.value.selectAll(false);
}

async function shareEntries(): Promise<void> {
  if (!workspaceInfo.value) {
    return;
  }
  copyPathLinkToClipboard(currentPath.value, workspaceInfo.value.handle, informationManager.value);
}

async function onDropAsReader(): Promise<void> {
  await informationManager.value.present(
    new Information({
      message: 'FoldersPage.ImportFile.noDropForReader',
      level: InformationLevel.Error,
    }),
    PresentationMode.Toast,
  );
}

function onSearchValueChange(pattern: string): void {
  searchInputValue.value = pattern;
  startSearch(pattern);
}

async function cancelSearch(): Promise<void> {
  if (searchAborter) {
    searchAborter.abort();
  }
  search.value = undefined;
}

async function startSearch(pattern: string): Promise<void> {
  await cancelSearch();
  await onSelectionCancel();
  if (pattern.length < 3 || !workspaceInfo.value) {
    return;
  }
  search.value = {
    results: [],
    pattern: pattern,
    active: true,
  };
  searchAborter = new AbortController();
  for await (const result of parsec.fileSearch(workspaceInfo.value.handle, '/', pattern, searchAborter.signal)) {
    if (search.value) {
      search.value.results.push(result);
    }
    await nextTick();
  }
  if (search.value) {
    search.value.active = false;
  }
  searchAborter = undefined;
}
</script>

<style scoped lang="scss">
.content-scroll {
  &::part(scroll) {
    -ms-overflow-style: -ms-autohiding-scrollbar;
  }

  &::part(background) {
    @include ms.responsive-breakpoint('sm') {
      background: var(--parsec-color-light-secondary-background);
    }
  }

  &::part(scroll) {
    @include ms.responsive-breakpoint('sm') {
      padding-top: 1rem;
    }
  }
}

.folder-container {
  display: flex;
  flex-direction: column;

  @include ms.responsive-breakpoint('sm') {
    position: sticky;
    z-index: 10;
    background: var(--parsec-color-light-secondary-white);
    box-shadow: var(--parsec-shadow-strong);
    border-radius: var(--parsec-radius-18) var(--parsec-radius-18) 0 0;
  }
}

.folder-container div:not(.no-files-content div) {
  height: 100%;
}

.loading-files-content {
  display: flex;
  justify-content: center;
}

.mobile-filters {
  @include ms.responsive-breakpoint('sm') {
    margin-top: 0 !important;
    height: auto !important;
  }

  &-buttons {
    @include ms.responsive-breakpoint('sm') {
      padding: 0.5rem 0.75rem;
      width: 100%;
      justify-content: flex-end;
      border-bottom: 1px solid var(--parsec-color-light-secondary-medium);
    }
  }
}

.search-mobile-container {
  height: auto !important;
  padding: 1.5rem 1rem 0.5rem 1rem !important;

  .search-input-mobile {
    height: auto !important;
    background: var(--parsec-color-light-secondary-background);

    &.input-content:focus-within:not(.form-input-disabled) {
      background: var(--parsec-color-light-secondary-white);
    }
  }
}

.workspace-role-tag {
  background: var(--parsec-color-light-secondary-white);
  padding: 0.25rem;
  border-radius: var(--parsec-radius-32);
  border: 1px solid var(--parsec-color-light-secondary-disabled);
}

.read-only-info {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.5rem;
  color: var(--parsec-color-light-primary-600);

  &__icon {
    width: 1rem;
    height: 1rem;
  }
}

.no-files {
  width: 100%;

  &-content {
    width: 100%;
    height: 100%;
    margin: auto;
    color: var(--parsec-color-light-secondary-grey);
    border-radius: var(--parsec-radius-8);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem 1rem;
  }
}

.error-list-folder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem 1rem;
  max-width: 30rem;
  margin: auto;

  &__image {
    width: 10rem;
    height: 10rem !important;
  }

  &__text {
    height: fit-content !important;
  }
}
</style>
